import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  Sparkles,
  Loader2,
  HelpCircle,
  ShieldAlert,
  Search,
} from "lucide-react";
import { api, getSocket, RESTRICTED_PATTERNS } from "./api";

export const QuestionInput = ({
  onSubmit,
  onChange,
  onSuggestionSelect,
  isProcessing,
  placeholder = "Ask your prediction question...",
  disabled = false,
  initialValue = "",
}) => {
  const [value, setValue] = useState(initialValue);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const justSelectedRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const spellDebounceRef = useRef(null);
  const spellAbortRef = useRef(null);
  const currentRequestIdRef = useRef(null);

  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState([]);
  const [isAutocompleteLoading, setIsAutocompleteLoading] = useState(false);
  const [autocompleteError, setAutocompleteError] = useState(null);
  const [aiRestricted, setAiRestricted] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [spellingCorrection, setSpellingCorrection] = useState(null);

  const hasSpellingCorrection = spellingCorrection !== null;
  const showDropdown =
    hasSpellingCorrection ||
    (showSuggestions &&
      (autocompleteSuggestions.length > 0 || isAutocompleteLoading));
  const totalDropdownItems =
    (hasSpellingCorrection ? 1 : 0) + autocompleteSuggestions.length;
  const socketCleanupRef = useRef(null);

  const triggerAutocomplete = useCallback((input) => {
    if (input.length < 3) {
      setShowSuggestions(false);
      setAutocompleteSuggestions([]);
      setAiRestricted(false);
      return;
    }

    const requestId = crypto.randomUUID();
    currentRequestIdRef.current = requestId;

    setAutocompleteSuggestions([]);
    setIsAutocompleteLoading(true);
    setAutocompleteError(null);
    setShowSuggestions(true);
    setSelectedIndex(-1);
    setAiRestricted(false);

    const socket = getSocket();
    if (socketCleanupRef.current) {
      socketCleanupRef.current();
      socketCleanupRef.current = null;
    }
    const suggestionEvent = `autocomplete:suggestion/${requestId}`;
    const restrictedEvent = `autocomplete:restricted/${requestId}`;
    const doneEvent = `autocomplete:done/${requestId}`;
    const errorEvent = `autocomplete:error/${requestId}`;

    const onSuggestion = ({ suggestion }) => {
      setAutocompleteSuggestions((prev) => {
        if (prev.length >= 5) return prev;
        return [...prev, suggestion];
      });
      setIsAutocompleteLoading(false);
      setShowSuggestions(true);
    };

    const onRestricted = ({ restrictedReason }) => {
      setAiRestricted(true);
      setAutocompleteSuggestions([]);
      setShowSuggestions(false);
      setIsAutocompleteLoading(false);
    };

    const onDone = () => {
      setIsAutocompleteLoading(false);
    };

    const onError = ({ error }) => {
      setAutocompleteError(error ?? "Something went wrong. Please try again.");
      setIsAutocompleteLoading(false);
    };

    socket.on(suggestionEvent, onSuggestion);
    socket.on(restrictedEvent, onRestricted);
    socket.on(doneEvent, onDone);
    socket.on(errorEvent, onError);
    socketCleanupRef.current = () => {
      socket.off(suggestionEvent, onSuggestion);
      socket.off(restrictedEvent, onRestricted);
      socket.off(doneEvent, onDone);
      socket.off(errorEvent, onError);
    };
    socket.emit("autocomplete:cancel");
    socket.emit("autocomplete:start", { input, requestId });
  }, []);

  const triggerSpellCheck = useCallback(async (input) => {
    if (input.trim().split(/\s+/).length < 3) {
      setSpellingCorrection(null);
      return;
    }

    if (spellAbortRef.current) spellAbortRef.current.abort();
    const controller = new AbortController();
    spellAbortRef.current = controller;

    try {
      const result = await api.checkSpelling(input, controller.signal);

      if (controller.signal.aborted) return;

      if (
        result?.hasErrors &&
        result?.correctedText &&
        result.correctedText !== input
      ) {
        setSpellingCorrection(result.correctedText);
        setIsAutocompleteLoading(false);
        setShowSuggestions(true);
      } else {
        setSpellingCorrection(null);
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      if (!controller.signal.aborted) setSpellingCorrection(null);
    }
  }, []);
  const debouncedAutocomplete = useCallback(
    (input) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (!input || input.length < 3) {
        setShowSuggestions(false);
        setAutocompleteSuggestions([]);
        return;
      }
      debounceTimerRef.current = setTimeout(
        () => triggerAutocomplete(input),
        600,
      );
    },
    [triggerAutocomplete],
  );

  const debouncedSpellCheck = useCallback(
    (input) => {
      if (spellDebounceRef.current) clearTimeout(spellDebounceRef.current);
      if (!input || input.trim().split(/\s+/).length < 3) {
        setSpellingCorrection(null);
        return;
      }
      spellDebounceRef.current = setTimeout(
        () => triggerSpellCheck(input),
        300,
      );
    },
    [triggerSpellCheck],
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (spellDebounceRef.current) clearTimeout(spellDebounceRef.current);
      if (spellAbortRef.current) spellAbortRef.current.abort();
      if (socketCleanupRef.current) socketCleanupRef.current();
      getSocket().emit("autocomplete:cancel");
    };
  }, []);

  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    debouncedSpellCheck(value);
    debouncedAutocomplete(value);
  }, [value, debouncedAutocomplete, debouncedSpellCheck]);

  useEffect(() => {
    const handlePointerDownOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
        setSpellingCorrection(null);
      }
    };
    document.addEventListener("pointerdown", handlePointerDownOutside);
    return () =>
      document.removeEventListener("pointerdown", handlePointerDownOutside);
  }, []);

  const isClientRestricted = useMemo(() => {
    return (
      value.trim().length > 5 &&
      RESTRICTED_PATTERNS.some((p) => p.test(value.trim()))
    );
  }, [value]);

  const isRestricted = isClientRestricted || aiRestricted;

  const handleChange = (newValue) => {
    setValue(newValue);
    if (onChange) onChange(newValue);
    if (aiRestricted && newValue.length < 3) {
      setAiRestricted(false);
    }
  };

  const handleSubmit = () => {
    if (value.trim() && !isProcessing) {
      onSubmit(value.trim());
      setValue("");
      setShowSuggestions(false);
      setAutocompleteSuggestions([]);
      setSpellingCorrection(null);
    }
  };

  const handleSpellingSelect = () => {
    if (!spellingCorrection) return;
    justSelectedRef.current = true;
    const corrected = spellingCorrection;
    setValue(corrected);
    if (onChange) onChange(corrected);
    if (onSuggestionSelect) onSuggestionSelect(corrected);
    setSpellingCorrection(null);
    setShowSuggestions(false);
    setAutocompleteSuggestions([]);
    setSelectedIndex(-1);
    requestAnimationFrame(() => inputRef.current && inputRef.current.focus());
  };

  const handleKeyDown = (e) => {
    if (showDropdown && totalDropdownItems > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < totalDropdownItems - 1 ? prev + 1 : prev,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === "Enter" && selectedIndex >= 0) {
        e.preventDefault();
        if (hasSpellingCorrection && selectedIndex === 0) {
          handleSpellingSelect();
        } else {
          const acIndex = hasSpellingCorrection
            ? selectedIndex - 1
            : selectedIndex;
          if (autocompleteSuggestions[acIndex]) {
            handleAutocompleteSuggestionSelect(
              autocompleteSuggestions[acIndex],
            );
          }
        }
        return;
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
        setSpellingCorrection(null);
        setSelectedIndex(-1);
      }
    }

    if (
      e.key === "Enter" &&
      !e.shiftKey &&
      selectedIndex < 0 &&
      !isRestricted
    ) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleAutocompleteSuggestionSelect = (suggestion) => {
    justSelectedRef.current = true;
    setShowSuggestions(false);
    setAutocompleteSuggestions([]);
    setSpellingCorrection(null);
    setSelectedIndex(-1);

    const questionText = suggestion.question || suggestion.title;
    setValue(questionText);
    if (onChange) onChange(questionText);
    if (onSuggestionSelect) onSuggestionSelect(questionText, suggestion);

    requestAnimationFrame(() => inputRef.current && inputRef.current.focus());
  };

  return (
    <div ref={containerRef} className="gm-qi-container">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="gm-qi-relative"
      >
        <div
          className={`gm-qi-card ${isRestricted ? "gm-qi-card--restricted" : ""} ${isFocused ? "gm-qi-card--active" : ""}`}
        >
          <textarea
            ref={inputRef}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              setIsFocused(true);
              if (
                value.length >= 3 &&
                (autocompleteSuggestions.length > 0 || hasSpellingCorrection)
              ) {
                setShowSuggestions(true);
              }
            }}
            onBlur={() => setIsFocused(false)}
            placeholder={placeholder}
            disabled={isProcessing}
            rows={2}
            autoComplete="off"
            className="gm-qi-textarea"
          />

          <div
            className={`gm-qi-footer ${isRestricted ? "gm-qi-footer--restricted" : ""}`}
          >
            <AnimatePresence mode="wait">
              {isRestricted ? (
                <motion.div
                  key="restricted"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="gm-qi-restricted-msg"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  >
                    <g clip-path="url(#clip0_1435_918)">
                      <path
                        d="M9 16.5C13.1421 16.5 16.5 13.1421 16.5 9C16.5 4.85786 13.1421 1.5 9 1.5C4.85786 1.5 1.5 4.85786 1.5 9C1.5 13.1421 4.85786 16.5 9 16.5Z"
                        stroke="#FF3300"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M9 12V9"
                        stroke="#FF3300"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M9 6H9.0075"
                        stroke="#FF3300"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_1435_918">
                        <rect width="18" height="18" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <span>This topic is not allowed on this platform</span>
                </motion.div>
              ) : value.length >= 3 ? (
                <motion.div
                  key="autocomplete-hint"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="gm-qi-hint"
                >
                  <Sparkles className="gm-icon-sm gm-text-primary" />
                  <span className="gm-qi-hint-text">
                    AI will generate outcomes
                  </span>
                </motion.div>
              ) : (
                <motion.div
                  key="sparkles"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="gm-qi-hint"
                >
                  <Sparkles className="gm-icon-sm gm-text-primary" />
                  <span className="gm-qi-hint-text">
                    AI will generate outcomes
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            <button
              onClick={handleSubmit}
              disabled={
                !value.trim() || isProcessing || disabled || isRestricted
              }
              className={`gm-qi-submit ${disabled || isRestricted ? "gm-qi-submit--disabled" : ""}`}
            >
              {isProcessing ? (
                <Loader2 className="gm-icon-sm gm-spin" />
              ) : (
                <>
                  <span>Create</span>
                  <Send className="gm-icon-sm" />
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {showDropdown && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.08, ease: "easeOut" }}
            className="gm-qi-dropdown"
          >
                {autocompleteSuggestions.length > 0 && (
                <div className="gm-qi-dropdown-header">
                  <div className="gm-qi-dropdown-header-inner">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <g clip-path="url(#clip0_1435_5945)">
                        <path
                          d="M7.99992 14.6666C11.6818 14.6666 14.6666 11.6818 14.6666 7.99992C14.6666 4.31802 11.6818 1.33325 7.99992 1.33325C4.31802 1.33325 1.33325 4.31802 1.33325 7.99992C1.33325 11.6818 4.31802 14.6666 7.99992 14.6666Z"
                          stroke="#E6FF00"
                          stroke-width="1.33333"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M6.06006 6.00014C6.21679 5.55458 6.52616 5.17887 6.93336 4.93956C7.34056 4.70024 7.81932 4.61276 8.28484 4.69261C8.75036 4.77246 9.1726 5.01449 9.47678 5.37582C9.78095 5.73716 9.94743 6.19448 9.94673 6.6668C9.94673 8.00014 7.94673 8.6668 7.94673 8.6668"
                          stroke="#E6FF00"
                          stroke-width="1.33333"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M8 11.3333H8.00667"
                          stroke="#E6FF00"
                          stroke-width="1.33333"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_1435_5945">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <span>Did you mean...</span>
                  </div>
                </div>
    )}
            {isAutocompleteLoading &&
              autocompleteSuggestions.length === 0 &&
              !hasSpellingCorrection && (
                <div className="gm-qi-dropdown-loading">
                  <Loader2 className="gm-icon-sm gm-spin gm-text-primary" />
                  <span>Finding market suggestions...</span>
                </div>
              )}

            {autocompleteError && (
              <div className="gm-qi-dropdown-error">{autocompleteError}</div>
            )}

            {!autocompleteError &&
              autocompleteSuggestions.map((suggestion, index) => {
                const dropdownIdx = hasSpellingCorrection ? index + 1 : index;
                return (
                  <motion.button
                    key={`${suggestion.question}-${index}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.15 }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAutocompleteSuggestionSelect(suggestion);
                    }}
                    onMouseEnter={() => setSelectedIndex(dropdownIdx)}
                    className={`gm-qi-dropdown-item ${selectedIndex === dropdownIdx ? "gm-qi-dropdown-item--active" : ""}`}
                  >
                    <div className="gm-qi-dropdown-item-main">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <g clip-path="url(#clip0_1435_5951)">
                          <path
                            d="M7.99992 14.6666C11.6818 14.6666 14.6666 11.6818 14.6666 7.99992C14.6666 4.31802 11.6818 1.33325 7.99992 1.33325C4.31802 1.33325 1.33325 4.31802 1.33325 7.99992C1.33325 11.6818 4.31802 14.6666 7.99992 14.6666Z"
                            stroke="#E6FF00"
                            stroke-width="1.33333"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M6.06006 6.00014C6.21679 5.55458 6.52616 5.17887 6.93336 4.93956C7.34056 4.70024 7.81932 4.61276 8.28484 4.69261C8.75036 4.77246 9.1726 5.01449 9.47678 5.37582C9.78095 5.73716 9.94743 6.19448 9.94673 6.6668C9.94673 8.00014 7.94673 8.6668 7.94673 8.6668"
                            stroke="#E6FF00"
                            stroke-width="1.33333"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M8 11.3333H8.00667"
                            stroke="#E6FF00"
                            stroke-width="1.33333"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_1435_5951">
                            <rect width="16" height="16" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      <span className="gm-qi-dropdown-item-text">
                        {suggestion.question || suggestion.title}
                      </span>
                    </div>
                  </motion.button>
                );
              })}

            {isAutocompleteLoading && autocompleteSuggestions.length > 0 && (
              <div className="gm-qi-dropdown-loading-more">
                <Loader2 className="gm-icon-xs gm-spin gm-text-primary" />
                <span>Loading more...</span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
